import type { Requisition } from '@customTypes/requisition';
import { getRequisitionStatusKey } from '@helpers/requisition';

import { getHomeOrderProgress, isHomeTerminalStatus } from './homeTimeline';

export interface HomeOrderEntry {
  requisition: Requisition;
}

export const buildHomeOrderEntries = (requisitions: Requisition[]): HomeOrderEntry[] =>
  requisitions.map(requisition => ({ requisition }));

const getOrderTime = (requisition: Requisition): number => {
  const time = Date.parse(requisition.submitted_at ?? '');
  return Number.isNaN(time) ? -Infinity : time;
};

/** Newest order date first. Does not mutate the input. */
export const sortHomeOrdersByOrderDate = (entries: HomeOrderEntry[]): HomeOrderEntry[] =>
  [...entries].sort((left, right) => getOrderTime(right.requisition) - getOrderTime(left.requisition));

/** Reported, or ended (canceled, denied, failed). The timeline has nothing left to show. */
export const isHomeOrderFinished = (requisition: Requisition): boolean =>
  getHomeOrderProgress(requisition).isFullyReported || isHomeTerminalStatus(getRequisitionStatusKey(requisition));

export interface HomeOrderSections {
  /** In-progress orders, newest first. When none are in progress, the newest finished order. */
  activeOrders: HomeOrderEntry[];
  /** Finished orders that are not on a card, newest first. */
  orderHistory: HomeOrderEntry[];
}

/**
 * Cards are orders still moving. The table is orders that are over.
 * When every order is finished, the newest stays a card so Home is not only a table.
 */
export const splitHomeOrders = (sortedEntries: HomeOrderEntry[]): HomeOrderSections => {
  const inProgress = sortedEntries.filter(entry => !isHomeOrderFinished(entry.requisition));

  if (inProgress.length > 0) {
    return {
      activeOrders: inProgress,
      orderHistory: sortedEntries.filter(entry => isHomeOrderFinished(entry.requisition)),
    };
  }

  return {
    activeOrders: sortedEntries.slice(0, 1),
    orderHistory: sortedEntries.slice(1),
  };
};
